import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { useKeyboard } from "../hooks/useKeyboard.js";
import { cn } from "../lib/utils.js";
import { DriveGallery } from "./DriveGallery.js";
import { ImageStage } from "./ImageStage.js";
import { InfoPanel } from "./InfoPanel.js";
import type { TagOptions } from "../lib/api.js";

interface PhotoModalProps {
  photo: PhotoRecord | null;
  onClose: () => void;
  onToggleLiked: (photo: PhotoRecord) => void;
  onUpdatePhoto?: (id: string, patch: Partial<PhotoRecord>) => void;
  currentUser?: string | null;
  /** Whether the current session has admin role. */
  isAdmin?: boolean;
  isReadOnly?: boolean;
  allPhotos?: PhotoRecord[];
  tagOptions?: TagOptions;
}

export function PhotoModal({
  photo,
  onClose,
  onToggleLiked,
  onUpdatePhoto,
  currentUser,
  isAdmin,
  isReadOnly,
  allPhotos,
  tagOptions,
}: PhotoModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [isImmersive, setIsImmersive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (photo) {
      setActiveIndex(0);
      setDirection(0);
      setShowGallery(false);
      setIsImmersive(false);
      setIsEditMode(false);
    }
  }, [photo?.id]);

  const thumbURLs = photo?.previewURLs?.thumbs ?? [];
  const fullURLs = photo?.previewURLs?.full ?? [];
  const count = thumbURLs.length || fullURLs.length;

  const goTo = useCallback((index: number) => {
    const nextIndex = Math.max(0, Math.min(Math.max(count - 1, 0), index));
    setDirection(nextIndex > activeIndex ? 1 : -1);
    setActiveIndex(nextIndex);
  }, [activeIndex, count]);

  const prev = useCallback(() => {
    if (activeIndex <= 0) return;
    setDirection(-1);
    setActiveIndex((current) => current - 1);
  }, [activeIndex]);

  const next = useCallback(() => {
    if (activeIndex >= count - 1) return;
    setDirection(1);
    setActiveIndex((current) => current + 1);
  }, [activeIndex, count]);

  useKeyboard({
    ArrowLeft: () => {
      if (!photo || showGallery) return;
      prev();
    },
    ArrowRight: () => {
      if (!photo || showGallery) return;
      next();
    },
    Escape: () => {
      if (!photo) return;
      if (showGallery) {
        setShowGallery(false);
        return;
      }
      if (isImmersive) {
        setIsImmersive(false);
        return;
      }
      onClose();
    },
  });

  if (!photo) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="photo-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-50 bg-[rgba(20,18,15,0.74)] backdrop-blur-[8px]"
        onClick={onClose}
      />

      <motion.div
        key={`photo-modal-${photo.id}`}
        initial={{ opacity: 0, y: 18, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.99 }}
        transition={{ duration: 0.24, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          "fixed inset-0 z-[51] flex flex-col overflow-hidden bg-[#f7f4ed]",
          "md:inset-3 md:rounded-[30px] md:shadow-[0_30px_120px_rgba(25,20,14,0.18)]",
          "lg:inset-4"
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-black/8 bg-white/86 text-ink-1 shadow-sm transition-colors hover:bg-white md:right-4 md:top-4"
          aria-label="Đóng modal"
        >
          <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="M2 2l10 10M12 2L2 12" />
          </svg>
        </button>

        {/* Mobile: single scrollable column. Desktop: side-by-side. */}
        <div className="overflow-y-auto md:flex md:h-full md:min-h-0 md:flex-row md:overflow-hidden">
          <ImageStage
            chatName={photo.chatName}
            thumbURLs={thumbURLs}
            fullURLs={fullURLs}
            activeIndex={activeIndex}
            direction={direction}
            isImmersive={isImmersive}
            onToggleImmersive={() => setIsImmersive((value) => !value)}
            onSelectIndex={goTo}
            onPrev={prev}
            onNext={next}
          />

          {!isImmersive && (
            <InfoPanel
              photo={photo}
              currentUser={currentUser}
              isAdmin={isAdmin}
              isReadOnly={isReadOnly}
              isEditMode={isEditMode}
              allPhotos={allPhotos ?? []}
              tagOptions={tagOptions}
              onToggleEditMode={() => setIsEditMode((value) => !value)}
              onToggleLiked={() => onToggleLiked(photo)}
              onOpenGallery={() => setShowGallery(true)}
              onSavedTags={(patch) => onUpdatePhoto?.(photo.id, patch)}
              onPhotoLinkSaved={(patch) => onUpdatePhoto?.(photo.id, patch)}
              onRename={isAdmin ? async (newName) => {
                onUpdatePhoto?.(photo.id, { chatName: newName });
                try {
                  const { patchPhoto } = await import("../lib/api.js");
                  await patchPhoto(photo.id, { adminFolderName: newName });
                } catch (err) {
                  console.error("[rename] failed:", err);
                  onUpdatePhoto?.(photo.id, { chatName: photo.chatName });
                }
              } : undefined}
            />
          )}
        </div>

        <AnimatePresence>
          {showGallery && (
            <DriveGallery
              recordId={photo.id}
              chatName={photo.chatName}
              driveLink={photo.driveLink?.trim() || undefined}
              isReadOnly={isReadOnly}
              onClose={() => setShowGallery(false)}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
